// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, useShellContext } from "@insite/client-framework/Components/IsInShell";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import FormActions from "@store/Components/Toolkit/FormActions";
import PersonLine from "@store/Components/Toolkit/Icons/Person/PersonLine";
import TextSeparator, { TextSeparatorPresentationProps } from "@store/Components/Toolkit/TextSeparator";
import { signInProps } from "@store/Themes/common/props/signIn";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { useIsNamedContext } from "@store/Framework/Components/NamedContext";
import { useCurrentUserIsGuest, useSettingsCollection } from "@store/Framework/Store/Context/ContextHooks";
import { useReturnUrl } from "@store/Framework/Store/Data/Pages/PageHooks";
import { usePageLinkByPageType } from "@store/Framework/Store/Links/LinksHooks";
import parse from "html-react-parser";
import React, { FC } from "react";

const enum fields {
    fullWidthButton = "fullWidthButton",
    showIcon = "showIcon",
    title = "title",
    text = "text",
}

interface OwnProps extends WidgetProps, HasShellContext {
    fields: {
        [fields.fullWidthButton]: boolean;
        [fields.showIcon]: boolean;
        [fields.title]: string;
        [fields.text]: string;
    };
}

type Props = OwnProps;

export interface SignInAsGuestStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    content?: InjectableCss;
    button?: ButtonPresentationProps;
    icon?: IconPresentationProps;
    separator?: TextSeparatorPresentationProps;
}

export const signInAsGuestStyles: SignInAsGuestStyles = {
    wrapper: signInProps.wrapper,
    icon: {
        ...signInProps.icon,
        src: PersonLine,
    },
    title: signInProps.title,
    button: { variant: "secondary" },
    separator: signInProps.separator,
};

const SignInAsGuestComponent: FC<Props> = ({ fields: { fullWidthButton, showIcon, title, text } }) => {
    const dispatch = useAppDispatch();
    const returnUrl = useReturnUrl();
    const { isEditing } = useShellContext();
    const { accountSettings } = useSettingsCollection();
    const allowGuestCheckout = accountSettings.allowGuestCheckout && !useCurrentUserIsGuest();
    const showGuestCheckout = returnUrl === usePageLinkByPageType("CheckoutShippingPage")?.url || isEditing;
    const isSignInOverlay = useIsNamedContext(constants.pageType.signInOverlay);
    const styles = React.useMemo(() => {
        if (!isSignInOverlay) {
            return signInAsGuestStyles;
        }

        return mergeProps<SignInAsGuestStyles>(signInAsGuestStyles, {
            wrapper: signInProps.overlayOverrides?.wrapper,
            icon: signInProps?.overlayOverrides?.icon,
            title: signInProps?.overlayOverrides?.title,
        });
    }, [isSignInOverlay]);

    if (!allowGuestCheckout && !showGuestCheckout) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        dispatch(signInAsGuest({ returnUrl }));
    };

    return (
        <>
            {isSignInOverlay && <TextSeparator {...styles.separator} />}
            <StyledWrapper {...styles.wrapper}>
                {showIcon && <Icon {...styles.icon} />}
                {title && <Typography {...styles.title}>{title}</Typography>}
                {text && <StyledWrapper {...styles.content}>{parse(text, parserOptions)}</StyledWrapper>}
                <FormActions fullWidth={fullWidthButton}>
                    <Button
                        {...styles.button}
                        onClick={handleCheckoutAsGuest}
                        data-test-selector="signInAsGuest_checkoutAsGuest"
                    >
                        {translate("Checkout as Guest")}
                    </Button>
                </FormActions>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: SignInAsGuestComponent,
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "user-unlock",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Checkout as Guest",
                fieldType: "Translatable",
            },
            {
                name: fields.text,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: `<p>You don't need to be registered to proceed to checkout. However, it is recommended for faster ordering as well as access to time and money-saving advantages.</p>\n\n<ul>\n<li>Order without creating an account</li>\n<li>Track your Orders</li>\n</ul>`,
                fieldType: "Translatable",
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
